import Rating from '@material-ui/lab/Rating';
import { ProductDefect } from 'app/core/api/product-defect/api-product-defect.types';
import { useInitiateChat } from 'app/core/api/repairer/api-repairer';
import { RepairerSearchItem } from 'app/core/api/repairer/api-repairer.types';
import { getErrorMsg } from 'app/core/errors/error-utils';
import AppImage from 'app/shared/components/app-image/AppImage';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  IWRepairerSvg,
} from 'app/shared/components/icon/icon-lib';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
// import AutoHeightAnimation from 'app/shared/frames/animation/AutoHeightAnimation';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { useWatchAsRef } from 'app/tools/hooks/misc-hooks';
import { useSubscription } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Observable } from 'rxjs';
import styled from 'styled-components/macro';

const RepairerContainer = styled.div`
  padding: ${styledFuncs.pad()};
`;
const RepairerBoundingBox = styled.div`
  ${styledMixins.roundCorners}
  background: white;
  box-shadow: 0 0 3px #ddd;
  overflow: hidden;
`;
const RepairerCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
// const PullUpHandle = styled.div`
//   width: 25px;
//   height: 3px;
//   border-radius: 1.5px;
//   margin: 0 auto;
//   position: absolute;
//   align-self: center;
//   top: 5px;
//   background: #e9e9e9;
// `;
const RepairerProfile = styled.div`
  display: flex;
  flex-flow: row;
  position: relative;
  font-size: 1rem;
  line-height: 1.5em;
  height: calc(4.5em + ${styledFuncs.pad()});
  align-items: center;
`;

const RepairerProfileImg = styled(AppImage)`
  flex: 0 0 auto;
  padding: ${styledFuncs.pad(0.5)};
  height: 100%;
  overflow: hidden;
  > * {
    ${styledMixins.roundCorners};
  }
`;

const RepairerProfileName = styled.div`
  margin: ${styledFuncs.pad()};
  margin-bottom: ${styledFuncs.pad(1.5)};
  margin-top: ${styledFuncs.pad(2)};
  flex: 1 1 auto;
  ${styledMixins.lineClamp(3)}
  > div {
    text-align: left;
    font-weight: 500;
    line-height: 1.4em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    &:nth-child(2) {
      margin-top: 2px;
      font-size: 0.8em;
      color: #999;
      font-style: italic;
    }
    &:nth-child(3) {
      margin-top: 2px;
      font-size: 0.8em;
      color: #000;
      font-weight: 400;
    }
  }
`;

const RepairerDetail = styled.div`
  display: none;
  > span {
    font-weight: 500;
    font-size: 14px;
    color: black;
    margin-left: ${styledFuncs.pad()};
  }
  > ul {
    margin: ${styledFuncs.pad(0.5)};
    list-style: none;
    padding-left: ${styledFuncs.pad(1.5)};
    > li {
      display: flex;
      align-items: center;
      margin-bottom: ${styledFuncs.pad(0.5)};
      > * {
        margin-right: ${styledFuncs.pad()};
      }
      p {
        font-weight: 500;
        font-size: 12px;
        margin: 0;
      }
    }
  }
`;
const RepairerRating = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${styledFuncs.pad(0.0)};
  margin-bottom: ${styledFuncs.pad(0.5)};
  > span {
    margin: 0 ${styledFuncs.pad(0.5)} 0 0;
    font-weight: 500;
    font-size: 14px;
    color: black;
  }
`;
const NewRepairer = styled.div`
  div {
    font-weight: 500;
    font-size: 10px;
    color: #6abc35;
  }
`;
const StartChatButton = styled(Button)`
  margin: ${styledFuncs.pad(2)} auto;
`;
const StartChatSliderButton = styled(Button)`
  margin: 0;
  height: calc(4.5em + ${styledFuncs.pad()});
  width: 100px;
  border-radius: 0 10px 10px 0;
  padding: 0;
`;
const DetailsIcon: React.FC<{ icon: IconLib }> = ({ icon }) => (
  <Icon inline emSize={1.5} {...{ icon }} />
);

export type SwipeActions = 'up' | 'down' | 'toggle';

type SlideViewProps = {
  swiped$: Observable<SwipeActions>;
  isCurrentSlide: boolean;
  repairer: RepairerSearchItem;
};
export default function SlideView({
  swiped$,
  isCurrentSlide,
  repairer,
}: SlideViewProps): JSX.Element {
  const [isSwipedUp, setSwipedUp] = useState(false);
  const [repairerId, setRepairerId] = useState<number>();
  const isCurrentSlideRef = useWatchAsRef(isCurrentSlide);

  useSubscription(swiped$, dir => {
    const isCurrent = isCurrentSlideRef.current;
    if (dir === 'up' && isCurrent) {
      setSwipedUp(false);
    }
    if (dir === 'down') {
      setSwipedUp(true);
    }
  });

  useEffect(() => {
    if (!isCurrentSlide) setTimeout(() => setSwipedUp(false), 100);
  }, [isCurrentSlide]);

  const showInfo = useShowInfo();
  const history = useHistory();
  const createdDefect = history.location.state as ProductDefect;

  const { request$: initiateChat, sending } = useInitiateChat();
  const openCaseId = repairer && repairer.openCaseId;

  const startChat = () => {
    if (openCaseId) {
      history.push(`${SharedRoutePath.CHAT}/${openCaseId}`);
    } else {
      setRepairerId(repairer.repairer);
      initiateChat({
        repairer: repairer.repairer,
        productDefect: createdDefect.id || 0,
        id: repairer.id,
      }).subscribe({
        next: chatItem => {
          history.push(
            `${SharedRoutePath.CHAT}/${chatItem?.id || ''}`,
            true
          );
        },
        error: (e: unknown) => {
          showInfo.showSnack({
            message: getErrorMsg(e),
          });
        },
      });
    }
  };

  return (
    <RepairerContainer>
      <RepairerBoundingBox>
        {/* <AutoHeightAnimation updateOn={isSwipedUp}> */}
        <RepairerCard>
          {/* <PullUpHandle /> */}
          <RepairerProfile>
            <RepairerProfileImg
              src={repairer.repairerDetails.logo}
              alt="repairer photo"
              fallbackImgNode={
                <IWRepairerSvg width="55" height="55" />
              }
            />

            <RepairerProfileName>
              <div>{repairer.repairerDetails.name}</div>
              {/* <div>{repairer.addrLine2 || repairer.addrLine1}</div> */}
              {/* <div>{repairer.locationDisplayName}</div> */}
              {!isSwipedUp && (
                <div>{repairer.distance.toFixed(1)} km away</div>
              )}
              <div>
                {repairer.rating ? (
                  <RepairerRating>
                    <span>{repairer.rating}</span>
                    <div>
                      <Rating
                        value={repairer.rating}
                        name={repairer.id.toString()}
                        readOnly
                      />
                    </div>
                    {/* <span>({repairer.ratingCount})</span> */}
                  </RepairerRating>
                ) : (
                  <NewRepairer>
                    <div
                      className="repairer"
                      style={{ fontSize: '10px', fontWeight: 500 }}
                    >
                      New Repairer
                    </div>
                  </NewRepairer>
                )}
              </div>
            </RepairerProfileName>
            {!isSwipedUp && (
              <div className="top-0 right-0 leading-none text-primary text-[0.7rem]">
                {/* {repairer.occupation || 'Repairer'} */}
                {repairerId === repairer.repairer && (
                  <StartChatSliderButton
                    slim
                    pending={sending}
                    onClick={startChat}
                  >
                    {openCaseId ? 'Go To Chat' : 'Start Chat'}
                  </StartChatSliderButton>
                )}
                {repairerId !== repairer.repairer && (
                  <StartChatSliderButton slim onClick={startChat}>
                    {openCaseId ? 'Go To Chat' : 'Start Chat'}
                  </StartChatSliderButton>
                )}
              </div>
            )}
          </RepairerProfile>
          {!isSwipedUp && (
            <RepairerDetail>
              {repairer.rating && (
                <RepairerRating>
                  <span>{repairer.rating}</span>
                  <div>
                    <Rating
                      value={repairer.rating}
                      name={repairer.id.toString()}
                    />
                  </div>
                  <span>({repairer.ratingCount})</span>
                </RepairerRating>
              )}
              {repairer.repairerDetails.overview && (
                <p className="mt-0.5 mb-2 ml-2 text-sm text-left">
                  {repairer.repairerDetails.overview}
                </p>
              )}
              <ul>
                {repairer.repairerDetails.website && (
                  <li>
                    <DetailsIcon icon={IconLib.HOME} />
                    <p className="touch-callout selectable">
                      {repairer.repairerDetails.website}
                    </p>
                  </li>
                )}
                {repairer.distance && (
                  <li>
                    <DetailsIcon icon={IconLib.ARROW_RIGHT} />
                    <p>{repairer.distance.toFixed(1)} km away</p>
                  </li>
                )}
              </ul>

              <StartChatButton
                slim
                pending={sending}
                onClick={startChat}
              >
                {openCaseId ? 'Go To Chat' : 'Start Chat'}
              </StartChatButton>
            </RepairerDetail>
          )}
        </RepairerCard>
        {/* </AutoHeightAnimation> */}
      </RepairerBoundingBox>
    </RepairerContainer>
  );
}
